import { SettingsProperty } from '../properties';
import { EventManager } from '../core/EventManager';
import { Logger } from '../core/Logger';

/**
 * 🛠️ DebugSystemManager - Handles all debug-related functionality
 * Extracted from EngineDemoApp to follow Single Responsibility Principle
 */
export class DebugSystemManager {
    private debugDialog: HTMLElement | null = null;
    private statsOverlay: HTMLElement | null = null;
    private performanceOverlay: HTMLElement | null = null;

    constructor(
        private eventManager: EventManager,
        private getAppSettings: () => any,
        private getSceneSettings: () => any,
        private getLayoutData: () => any
    ) {
        this.setupDebugControls();
    }

    /**
     * Setup debug controls (F4 key listener)
     */
    private setupDebugControls(): void {
        document.addEventListener('keydown', (event) => {
            if (event.key === 'F4' && !event.ctrlKey && !event.shiftKey && !event.altKey) {
                event.preventDefault();
                this.toggleDebugDialog();
            }
        });
    }

    /**
     * Toggle debug dialog visibility
     */
    public toggleDebugDialog(): void {
        if (this.debugDialog) {
            this.hideDebugDialog();
        } else {
            this.showDebugDialog();
        }
    }

    /**
     * Show debug dialog with property values
     */
    private showDebugDialog(): void {
        if (this.debugDialog) return;

        // Create debug dialog
        this.debugDialog = document.createElement('div');
        this.debugDialog.style.cssText = `
            position: fixed;
            top: 50px;
            left: 50px;
            width: 800px;
            max-width: 90vw;
            max-height: 80vh;
            background: linear-gradient(135deg, 
                rgba(15, 15, 35, 0.98) 0%,
                rgba(25, 15, 45, 0.95) 25%,
                rgba(35, 25, 55, 0.95) 50%,
                rgba(25, 15, 45, 0.95) 75%,
                rgba(15, 15, 35, 0.98) 100%
            );
            border-radius: 12px;
            backdrop-filter: blur(20px) saturate(180%);
            border: 1px solid rgba(255, 255, 255, 0.1);
            box-shadow: 0 25px 50px rgba(0, 0, 0, 0.7);
            color: white;
            font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
            font-size: 12px;
            z-index: 15000;
            overflow: hidden;
            display: flex;
            flex-direction: column;
        `;

        // Header
        const header = this.createDialogHeader();
        
        // Content
        const content = this.createDialogContent();

        this.debugDialog.appendChild(header);
        this.debugDialog.appendChild(content);

        // Add to DOM
        document.body.appendChild(this.debugDialog);

        //Logger.Instance.Info('🛠️ Debug Dialog opened - showing current property values', 'Debug');
    }

    /**
     * Create dialog header with title and close button
     */
    private createDialogHeader(): HTMLElement {
        const header = document.createElement('div');
        header.style.cssText = `
            padding: 15px 20px;
            background: rgba(0, 0, 0, 0.3);
            border-bottom: 1px solid rgba(255, 255, 255, 0.1);
            display: flex;
            justify-content: space-between;
            align-items: center;
            font-size: 14px;
            font-weight: bold;
        `;

        const title = document.createElement('span');
        title.textContent = '🛠️ Debug Properties - F4 to toggle';
        title.style.cssText = `
            background: linear-gradient(45deg, #9370DB, #4B0082, #8A2BE2);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
        `;

        const closeBtn = document.createElement('button');
        closeBtn.textContent = '✕';
        closeBtn.style.cssText = `
            background: none;
            border: none;
            color: rgba(255, 255, 255, 0.7);
            font-size: 18px;
            cursor: pointer;
            padding: 0;
            width: 20px;
            height: 20px;
            display: flex;
            align-items: center;
            justify-content: center;
        `;
        closeBtn.addEventListener('click', () => this.hideDebugDialog());

        header.appendChild(title);
        header.appendChild(closeBtn);

        return header;
    }

    /**
     * Create dialog content with filter and property tables
     */
    private createDialogContent(): HTMLElement {
        const content = document.createElement('div');
        content.style.cssText = `
            padding: 20px;
            overflow-y: auto;
            flex: 1;
            line-height: 1.4;
        `;

        // Create filter textbox
        const filterContainer = this.createFilterContainer();
        
        // Content container for filtered results
        const contentContainer = document.createElement('div');
        contentContainer.id = 'debug-content-container';
        
        // Generate debug content
        contentContainer.innerHTML = this.generateDebugContent();
        
        // Add filter functionality
        const filterInput = filterContainer.querySelector('input') as HTMLInputElement;
        filterInput.addEventListener('input', (e) => {
            const filterText = (e.target as HTMLInputElement).value.toLowerCase();
            contentContainer.innerHTML = this.generateDebugContent(filterText);
        });
        
        content.appendChild(filterContainer);
        content.appendChild(contentContainer);

        return content;
    }

    /**
     * Create filter input container
     */
    private createFilterContainer(): HTMLElement {
        const filterContainer = document.createElement('div');
        filterContainer.style.cssText = `
            margin-bottom: 15px;
            position: sticky;
            top: 0;
            background: rgba(0, 0, 0, 0.8);
            padding: 10px;
            border-radius: 8px;
            z-index: 1;
        `;

        const filterInput = document.createElement('input');
        filterInput.type = 'text';
        filterInput.placeholder = '🔍 Filter properties...';
        filterInput.style.cssText = `
            width: 100%;
            padding: 8px 12px;
            background: rgba(255, 255, 255, 0.1);
            border: 1px solid rgba(255, 255, 255, 0.2);
            border-radius: 6px;
            color: white;
            font-size: 13px;
            font-family: 'Consolas', monospace;
        `;

        filterContainer.appendChild(filterInput);
        return filterContainer;
    }

    /**
     * Hide debug dialog
     */
    private hideDebugDialog(): void {
        if (this.debugDialog) {
            document.body.removeChild(this.debugDialog);
            this.debugDialog = null;
        }
    }

    /**
     * Generate debug content with optional filter
     */
    private generateDebugContent(filter?: string): string {
        let html = '';

        // AppSettings Section
        html += this.generatePropertySection('', this.getAppSettings(), '#4CAF50', filter);

        // SceneSettings Section  
        html += this.generatePropertySection('', this.getSceneSettings(), '#2196F3', filter);

        // LayoutData Section
        html += this.generatePropertySection('', this.getLayoutData(), '#FF9800', filter);

        return html;
    }

    /**
     * Generate a property section with filtering and highlighting
     */
    private generatePropertySection(title: string, dataObject: any, color: string, filter?: string): string {
        if (!dataObject) {
            return '';
        }

        // Get all properties from the object
        const properties = this.extractPropertiesFromObject(dataObject);

        // Apply filter if provided
        let filteredProperties = properties;
        if (filter && filter.trim() !== '') {
            filteredProperties = properties.filter(prop => 
                prop.name.toLowerCase().includes(filter.toLowerCase()) ||
                (prop.group && prop.group.toLowerCase().includes(filter.toLowerCase()))
            );
        }

        // If no properties match the filter, return empty
        if (filteredProperties.length === 0) {
            return '';
        }

        let html = `<div style="margin-bottom: 15px;">
            <div style="background: rgba(0,0,0,0.2); border-radius: 8px; padding: 15px; border: 1px solid rgba(255,255,255,0.05);">
                <table style="width: 100%; border-collapse: collapse;">
                    <thead>
                        <tr style="border-bottom: 1px solid rgba(255,255,255,0.1);">
                            <th style="text-align: left; padding: 8px; color: rgba(255,255,255,0.8); font-weight: bold;">Property</th>
                            <th style="text-align: left; padding: 8px; color: rgba(255,255,255,0.8); font-weight: bold;">Group</th>
                            <th style="text-align: left; padding: 8px; color: rgba(255,255,255,0.8); font-weight: bold;">Type</th>
                            <th style="text-align: left; padding: 8px; color: rgba(255,255,255,0.8); font-weight: bold;">Current Value</th>
                        </tr>
                    </thead>
                    <tbody>`;

        // Sort properties by group, then by name
        filteredProperties.sort((a, b) => {
            const groupA = a.group || 'Unknown';
            const groupB = b.group || 'Unknown';
            if (groupA !== groupB) {
                return groupA.localeCompare(groupB);
            }
            return a.name.localeCompare(b.name);
        });

        let currentGroup = '';
        filteredProperties.forEach(prop => {
            const group = prop.group || 'Unknown';
            const groupChanged = group !== currentGroup;
            currentGroup = group;

            const value = this.formatPropertyValue(prop.value);
            const type = this.getPropertyType(prop);

            // Highlight matched text if filter is applied
            let displayName = prop.name;
            let displayGroup = group;
            if (filter && filter.trim() !== '') {
                const filterRegex = new RegExp(`(${filter})`, 'gi');
                displayName = prop.name.replace(filterRegex, '<mark style="background: rgba(147, 112, 219, 0.6); color: white; padding: 0 2px; border-radius: 2px;">$1</mark>');
                displayGroup = group.replace(filterRegex, '<mark style="background: rgba(147, 112, 219, 0.6); color: white; padding: 0 2px; border-radius: 2px;">$1</mark>');
            }

            html += `<tr style="border-bottom: 1px solid rgba(255,255,255,0.03); ${groupChanged ? 'border-top: 1px solid rgba(255,255,255,0.1);' : ''}">
                <td style="padding: 6px 8px; color: #ffffff; font-weight: ${groupChanged ? 'bold' : 'normal'};">${displayName}</td>
                <td style="padding: 6px 8px; color: rgba(255,255,255,0.6); font-size: 11px;">${displayGroup}</td>
                <td style="padding: 6px 8px; color: rgba(255,255,255,0.5); font-size: 11px;">${type}</td>
                <td style="padding: 6px 8px; color: ${color}; font-family: monospace; font-size: 11px;">${value}</td>
            </tr>`;
        });

        html += `</tbody></table></div></div>`;
        return html;
    }

    /**
     * Extract all SettingsProperty instances from an object
     */
    private extractPropertiesFromObject(obj: any): SettingsProperty<any>[] {
        const properties: SettingsProperty<any>[] = [];

        // Recursively find all SettingsProperty instances
        const findProperties = (currentObj: any, visited = new Set()) => {
            if (!currentObj || visited.has(currentObj)) return;
            visited.add(currentObj);

            for (const key in currentObj) {
                if (currentObj.hasOwnProperty(key)) {
                    const value = currentObj[key];

                    // Check if it's a SettingsProperty
                    if (value && typeof value === 'object' && 
                        ('name' in value) && ('value' in value) && 
                        ('group' in value) && typeof value.value !== 'undefined') {
                        properties.push(value);
                    } else if (value && typeof value === 'object' && !Array.isArray(value)) {
                        // Recursively search in nested objects
                        findProperties(value, visited);
                    }
                }
            }
        };

        findProperties(obj);
        return properties;
    }

    /**
     * Format property value for display
     */
    private formatPropertyValue(value: any): string {
        if (value === null || value === undefined) {
            return '<span style="color: rgba(255,255,255,0.4);">null</span>';
        }

        if (typeof value === 'boolean') {
            return value ? '<span style="color: #4CAF50;">true</span>' : '<span style="color: #f44336;">false</span>';
        }

        if (typeof value === 'number') {
            return value.toString();
        }

        if (typeof value === 'string') {
            return `"${value}"`;
        }

        if (value && typeof value === 'object') {
            // Handle Vector3
            if ('x' in value && 'y' in value && 'z' in value && !('w' in value)) {
                return `(${value.x.toFixed(2)}, ${value.y.toFixed(2)}, ${value.z.toFixed(2)})`;
            }

            // Handle Quaternion
            if ('x' in value && 'y' in value && 'z' in value && 'w' in value) {
                return `(${value.x.toFixed(2)}, ${value.y.toFixed(2)}, ${value.z.toFixed(2)}, ${value.w.toFixed(2)})`;
            }

            // Handle Color
            if ('r' in value && 'g' in value && 'b' in value) {
                const r = Math.round(value.r * 255);
                const g = Math.round(value.g * 255);
                const b = Math.round(value.b * 255);
                return `<span style="color: rgb(${r},${g},${b});">rgb(${r}, ${g}, ${b})</span>`;
            }

            // Handle arrays
            if (Array.isArray(value)) {
                return `[${value.length} items]`;
            }

            // Generic object
            return `{${Object.keys(value).length} props}`;
        }

        return String(value);
    }

    /**
     * Get property type as string
     */
    private getPropertyType(prop: SettingsProperty<any>): string {
        if (!prop.value) return 'unknown';

        const value = prop.value;

        if (typeof value === 'boolean') return 'bool';
        if (typeof value === 'number') return 'number';
        if (typeof value === 'string') return 'string';

        if (value && typeof value === 'object') {
            if ('x' in value && 'y' in value && 'z' in value && !('w' in value)) return 'Vector3';
            if ('x' in value && 'y' in value && 'z' in value && 'w' in value) return 'Quaternion';
            if ('r' in value && 'g' in value && 'b' in value) return 'Color';
            if (Array.isArray(value)) return 'Array';
            return 'Object';
        }

        return typeof value;
    }

    /**
     * Cleanup resources
     */
    public dispose(): void {
        this.hideDebugDialog();
        // Remove event listeners if needed
    }
} 